import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ArrowRightIcon } from 'shared/ui/icons';
import { PdfViewer, PDF_VIEWER_TOOLBAR_HEIGHT } from 'shared/ui/pdf-viewer';
import { CustomButton } from 'shared/ui/custom-button';
import COLORS from 'shared/design-tokens/colors';
import useContainerHeight from 'shared/hooks/useContainerHeight';
import {
  resetUploadState,
  setProgressModalOpen,
} from 'store/buku-tarif/bukuTarifSlice';
import { ModalConfirmUpload } from '../components/modal-confirm-upload';
import { ModalProgressUpload } from '../components/modal-progress-upload';
import { useSubmitUploadTariffBook } from '../hooks/useSubmitUploadTariffBook';

const UploadTariffBookPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const uploadFormData = useSelector((state) => state.bukuTarif.uploadFormData);
  const filepathBlobUrl = useSelector(
    (state) => state.bukuTarif.filepathBlobUrl
  );
  const isProgressModalOpen = useSelector(
    (state) => state.bukuTarif.isProgressModalOpen
  );

  const [isModalOpenConfirm, setIsModalOpenConfirm] = useState(false);
  const { handleSubmit, loading } = useSubmitUploadTariffBook();
  const { ref: pdfContainerRef, height: pdfHeight } = useContainerHeight();

  const handleCancel = () => {
    dispatch(resetUploadState());
    navigate('/buku-tarif');
  };

  useEffect(() => {
    if (!uploadFormData?.id) {
      toast.error(t('file_required'));
      navigate('/buku-tarif');
    }
  }, []);

  useEffect(() => {
    return () => {
      if (filepathBlobUrl) {
        URL.revokeObjectURL(filepathBlobUrl);
      }
    };
  }, [filepathBlobUrl]);

  return (
    <>
      <main className="w-full h-screen flex flex-col overflow-auto">
        <UploadHeader filename={uploadFormData?.file?.filename} />
        <section
          ref={pdfContainerRef}
          className="flex-1 min-h-0 px-6 bg-neutral-25"
        >
          {filepathBlobUrl && pdfHeight > 0 ? (
            <PdfViewer
              fileUrl={filepathBlobUrl}
              heightCard={`${pdfHeight}px`}
              heightPdf={`${pdfHeight - PDF_VIEWER_TOOLBAR_HEIGHT}px`}
            />
          ) : (
            <p>{t('no_pdf_available')}</p>
          )}
        </section>
        <UploadActionFooter
          loading={loading}
          onCancel={handleCancel}
          onUpload={() => setIsModalOpenConfirm(true)}
        />
      </main>

      <ModalConfirmUpload
        isOpen={isModalOpenConfirm}
        onClose={() => setIsModalOpenConfirm(false)}
        onConfirm={() => handleSubmit(uploadFormData?.id)}
        isLoading={loading}
        name={uploadFormData?.file?.filename}
      />
      <ModalProgressUpload
        isOpen={isProgressModalOpen}
        onClose={() => dispatch(setProgressModalOpen(false))}
      />
    </>
  );
};

function UploadHeader({ filename }) {
  const { t } = useTranslation('common');

  return (
    <header className="flex-shrink-0 flex justify-between items-center pr-8">
      <div className="w-full pt-8 px-6 pb-4 gap-y-2 flex flex-wrap items-center">
        <nav aria-label="Breadcrumb" className="w-auto flex items-center gap-2">
          <span className="text-sm font-normal text-slate-500 cursor-pointer hover:text-slate-800">
            {t('tarif_book')}
          </span>
          <ArrowRightIcon color={COLORS.slate500} aria-hidden="true" />
          <span className="text-sm font-medium text-slate-700">
            {t('upload_tariff_book')}
          </span>
        </nav>
        <div className="w-full flex flex-wrap">
          <div className="w-1/2 flex items-center">
            <h1 className="font-semibold text-2xl">{filename}</h1>
          </div>
        </div>
      </div>
    </header>
  );
}

function UploadActionFooter({ loading, onCancel, onUpload }) {
  const { t } = useTranslation('common');

  return (
    <footer className="flex-shrink-0 w-full flex justify-end mt-4 gap-3 px-6 pb-4">
      <CustomButton
        type="outline"
        size="sm"
        onClick={onCancel}
        isLoading={loading}
      >
        {t('cancel')}
      </CustomButton>
      <CustomButton
        type="primary"
        size="sm"
        onClick={onUpload}
        isLoading={loading}
      >
        {t('upload')}
      </CustomButton>
    </footer>
  );
}

export default UploadTariffBookPage;
